import { useState, useEffect, useCallback } from 'react';
import { message } from 'antd';
import type { PersonnelDetail } from '@/types/personnelDetail';
import { usersService } from '@/services/users.service';
import { departmentsService } from '@/services/departments.service';
import { handleApiError } from '@/utils/errorHandler';
import { loadAvatarFromData } from '@/utils/avatarLoader';
import { mapUserDetailToPersonnelDetail } from '@/features/personnel/personnelDataMapper';
import { exportPersonnelToExcel } from '@/features/personnel/exportPersonnelExcel';
import { useAuth } from '@/contexts/AuthContext';
import { useNotificationSocket } from '@/hooks/useNotificationSocket';

export interface UsePersonalProfileResult {
  // data
  detailData: PersonnelDetail | null;
  loading: boolean;
  error: string | null;
  avatarUri: string | null;

  // actions
  handleRefresh: () => void;
  handleExport: () => Promise<void>;
  formatFieldValue: (fieldName: string, value: any) => string;
}

export const usePersonalProfile = (): UsePersonalProfileResult => {
  const { user } = useAuth();
  const [detailData, setDetailData] = useState<PersonnelDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [avatarUri, setAvatarUri] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  useNotificationSocket({
    onNewNotification: (notification) => {
      if (notification.title === 'Yêu cầu cập nhật đã được phê duyệt') {
        setRefreshKey((previous) => previous + 1);
      }
    },
  });

  // Fetch user detail từ API - chỉ lấy thông tin của user đang đăng nhập
  const fetchPersonalProfile = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      if (!user?.id) {
        const errorMsg = 'Không tìm thấy thông tin người dùng';
        setError(errorMsg);
        message.error(errorMsg);
        return;
      }

      const userId = parseInt(user.id.toString(), 10);

      if (!userId || isNaN(userId)) {
        const errorMsg = 'Không tìm thấy thông tin nhân sự';
        setError(errorMsg);
        message.error(errorMsg);
        return;
      }

      // Fetch user detail, departments và các dữ liệu liên quan song song để tăng tốc độ
      const [
        userDetail,
        departmentsResponse,
        educationHistory,
        workExperience,
        familyMembers,
        certificates,
        partyHistory,
        idCards,
        personalFiles,
        horoscope,
      ] = await Promise.all([
        // Hồ sơ cá nhân luôn đọc qua endpoint tự phục vụ, không yêu cầu
        // quyền user:read dành cho việc xem hồ sơ người khác.
        usersService.getProfile(),
        departmentsService.getDepartments().catch(() => ({ data: [] })),
        usersService.getEducationHistory(userId).catch((err) => {
          console.warn('Failed to fetch education history:', err);
          return [];
        }),
        usersService.getWorkExperience(userId).catch((err) => {
          console.warn('Failed to fetch work experience:', err);
          return [];
        }),
        usersService.getFamilyMembers(userId).catch((err) => {
          console.warn('Failed to fetch family members:', err);
          return [];
        }),
        usersService.getCertificates(userId).catch((err) => {
          console.warn('Failed to fetch certificates:', err);
          return [];
        }),
        usersService.getPartyHistory(userId).catch((err) => {
          console.warn('Failed to fetch party history:', err);
          return [];
        }),
        usersService.getIdCards(userId).catch((err) => {
          console.warn('Failed to fetch ID cards:', err);
          return [];
        }),
        usersService.getPersonalFiles(userId).catch((err) => {
          console.warn('Failed to fetch personal files:', err);
          return [];
        }),
        usersService.getHoroscope(userId).catch((err) => {
          console.warn('Failed to fetch horoscope:', err);
          return null;
        }),
      ]);

      // Lấy tên phòng ban
      let departmentName = '';
      if (userDetail.departmentId) {
        const department = departmentsResponse.data.find((d: any) => d.id === userDetail.departmentId);
        departmentName = department?.name || '';
      }

      const isApprovedCard = (card: { status?: string; isApproved?: boolean }) =>
        (card.status || '').toLowerCase() === 'approved' || card.isApproved === true;

      const approvedIdCards = idCards.filter(isApprovedCard);

      const withVersion = (url?: string) => (url ? encodeURI(url) : undefined);

      // Tìm ID card images (chỉ hiển thị bản đã duyệt)
      const idCardFront = withVersion(approvedIdCards.find((card) => card.documentType === 'id_card_front')?.fileUrl);
      const idCardBack = withVersion(approvedIdCards.find((card) => card.documentType === 'id_card_back')?.fileUrl);
      const passportCover = withVersion(approvedIdCards.find((card) => card.documentType === 'passport_cover')?.fileUrl);
      const passportPhoto = withVersion(
        approvedIdCards.find(
          (card) => card.documentType === 'passport_additional' || card.documentType === 'passport_photo',
        )?.fileUrl,
      );

      // Map sang PersonnelDetail format
      const mappedData = mapUserDetailToPersonnelDetail(userDetail, {
        educationHistory: educationHistory.map((edu) => ({
          trainingInstitution: edu.trainingInstitution,
          major: edu.major,
          trainingForm: edu.trainingForm,
          educationLevel: edu.educationLevel,
          fromDate: edu.fromDate,
          toDate: edu.toDate,
        })),
        workExperience: workExperience.map((exp) => ({
          company: exp.company,
          position: exp.position,
          department: exp.department,
          salary: exp.salary,
          fromDate: exp.fromDate,
          toDate: exp.toDate,
        })),
        familyMembers: familyMembers.map((member) => ({
          relationship: member.relationship,
          fullName: member.fullName,
          birthYear: member.birthYear,
          phone: member.phone,
          occupation: member.occupation,
          workplace: member.workplace,
          address: member.address,
        })),
        certificates: certificates.map((cert) => ({
          id: cert.id,
          certificateNumber: cert.certificateNumber,
          type: cert.certificateType,
          issueDate: cert.fromDate,
          expiryDate: cert.toDate,
          notes: cert.notes,
          issuingUnit: cert.issuingUnit,
          attachments: cert.attachments || [],
        })),
        partyHistory: partyHistory.map((party) => ({
          cardNumber: party.cardNumber,
          form: party.form,
          fromDate: party.fromDate,
          toDate: party.toDate,
          admissionPlace: party.admissionPlace,
          transferPlace: party.transferPlace,
        })),
        personalFiles: personalFiles.map((file) => ({
          fileName: file.fileName,
          fileUrl: file.fileUrl,
        })),
        idCardFront,
        idCardBack,
        passportCover,
        passportPhoto,
        horoscope: horoscope?.imageUrl,
      });

      // Cập nhật department từ department name
      if (departmentName && mappedData.currentJob) {
        mappedData.currentJob.department = departmentName;
      }

      setDetailData(mappedData);

      // Load avatar từ detailData.avatar
      try {
        const avatar = await loadAvatarFromData(mappedData.avatar);
        setAvatarUri(avatar);
      } catch (err) {
        console.error('Error loading avatar:', err);
        setAvatarUri(null);
      }
    } catch (err: any) {
      console.error('Error fetching personal profile:', err);
      const errorMsg = err?.message || 'Không thể tải thông tin hồ sơ';
      setError(errorMsg);
      handleApiError(err, errorMsg);
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    fetchPersonalProfile();
  }, [fetchPersonalProfile, refreshKey]);

  const handleRefresh = useCallback(() => {
    setRefreshKey(prev => prev + 1);
  }, []);

  const handleExport = useCallback(async () => {
    if (!detailData) {
      message.warning('Không có dữ liệu để xuất file');
      return;
    }

    try {
      // Tạo tên file: TenNhanSu_MaNhanSu (hỗ trợ tiếng Việt)
      const fullName = (detailData.fullName || 'NhanSu').replace(/\s+/g, '_').replace(/[<>:"/\\|?*]/g, '');
      const employeeCode = (detailData.employeeCode || 'MaNS').replace(/\s+/g, '_').replace(/[<>:"/\\|?*]/g, '');
      const fileName = `${fullName}_${employeeCode}.xlsx`;
      await exportPersonnelToExcel(detailData, fileName);
      message.success('Xuất file Excel thành công');
    } catch (err: any) {
      console.error('Error exporting file:', err);
      handleApiError(err, 'Không thể xuất file: ' + (err.message || 'Lỗi không xác định'));
    }
  }, [detailData]);

  // Helper function để format giá trị từ tiếng Anh sang tiếng Việt
  const formatFieldValue = useCallback((fieldName: string, value: any): string => {
    if (!value || value === '') return String(value || '');

    const strValue = String(value).toLowerCase().trim();

    // Format gender
    if (fieldName === 'gender') {
      const genderMap: Record<string, string> = {
        'male': 'Nam',
        'female': 'Nữ',
        'other': 'Khác',
      };
      return genderMap[strValue] || String(value);
    }

    // Format marital status
    if (fieldName === 'maritalStatus') {
      const maritalStatusMap: Record<string, string> = {
        'single': 'Độc thân',
        'married': 'Đã kết hôn',
        'divorced': 'Ly hôn',
        'widowed': 'Góa',
      };
      return maritalStatusMap[strValue] || String(value);
    }

    // Format military service
    if (fieldName === 'militaryService') {
      const militaryServiceMap: Record<string, string> = {
        'yes': 'Đã hoàn thành',
        'no': 'Hoãn nghĩa vụ',
        'exempt': 'Miễn nghĩa vụ',
      };
      return militaryServiceMap[strValue] || String(value);
    }

    // Format labor type
    if (fieldName === 'laborType') {
      const laborTypeMap: Record<string, string> = {
        'full-time': 'Toàn thời gian',
        'fulltime': 'Toàn thời gian',
        'part-time': 'Bán thời gian',
        'parttime': 'Bán thời gian',
        'contract': 'Hợp đồng',
        'intern': 'Thực tập',
        'internship': 'Thực tập',
      };
      return laborTypeMap[strValue] || String(value);
    }

    // Format general education level
    if (fieldName === 'educationLevel' || fieldName === 'generalEducationLevel' || fieldName === 'highestEducationLevel') {
      const generalEducationLevelMap: Record<string, string> = {
        'primary': 'Tiểu học',
        'secondary': 'Trung học cơ sở',
        'highschool': 'Trung học phổ thông',
      };
      return generalEducationLevelMap[strValue] || String(value);
    }

    // Format academic education level
    if (fieldName === 'academicEducationLevel') {
      const academicEducationLevelMap: Record<string, string> = {
        'secondary': 'Trung cấp',
        'college': 'Cao đẳng',
        'university': 'Đại học',
        'graduate': 'Sau đại học',
      };
      return academicEducationLevelMap[strValue] || String(value);
    }

    // Format ethnicity
    if (fieldName === 'ethnicity') {
      const ethnicityMap: Record<string, string> = {
        'kinh': 'Kinh',
        'tay': 'Tày',
        'thai': 'Thái',
        'muong': 'Mường',
        'khmer': 'Khmer',
        'hoa': 'Hoa',
        'nung': 'Nùng',
        'hmong': 'H\'Mông',
        'dao': 'Dao',
        'giarai': 'Gia Rai',
        'ede': 'Ê Đê',
        'bana': 'Ba Na',
        'xodang': 'Xơ Đăng',
        'sanchay': 'Sán Chay',
        'coho': 'Cơ Ho',
        'cham': 'Chăm',
        'sandiu': 'Sán Dìu',
        'hre': 'Hrê',
        'mnong': 'M\'Nông',
        'raglai': 'Ra Glai',
        'stieng': 'Stiêng',
        'bruvankieu': 'Bru - Vân Kiều',
        'tho': 'Thổ',
        'giay': 'Giáy',
        'co': 'Cơ Tu',
        'taoi': 'Tà Ôi',
        'ma': 'Mạ',
        'khmu': 'Khơ Mú',
        'boy': 'Bố Y',
        'xinhmun': 'Xinh Mun',
        'cong': 'Cống',
        'hanhi': 'Hà Nhì',
        'lahu': 'La Hủ',
        'phula': 'Phù Lá',
        'lachi': 'La Chí',
        'pathen': 'Pa Then',
        'churu': 'Chu Ru',
        'lao': 'Lào',
        'laha': 'La Ha',
        'khang': 'Kháng',
        'lu': 'Lự',
        'lolo': 'Lô Lô',
        'mang': 'Mảng',
        'phathay': 'Pà Thẻn',
        'colao': 'Cờ Lao',
        'brau': 'Brâu',
        'odu': 'Ơ Đu',
        'romam': 'Rơ Măm',
        'sila': 'Si La',
        'pupeo': 'Pu Péo',
        'other': 'Khác',
      };
      return ethnicityMap[strValue] || String(value);
    }

    return String(value);
  }, []);

  return {
    // data
    detailData,
    loading,
    error,
    avatarUri,

    // actions
    handleRefresh,
    handleExport,
    formatFieldValue,
  };
};
